// ─────────────────────────────────────────────────────────────────────
// PLAYER DATABASE — the seeded demo/fallback player DB + the "effective" DB
// merger. Extracted verbatim from the monolith (legacy/App.jsx) in PR-AN.
//
//  - PLAYER_DB: a deterministic 200-player seed (seeded PRNG, stable across
//    loads) used as the fallback when no real players are loaded from Storage.
//  - getEffectivePlayerDB(archive, playersList): merges a player list (or the
//    seed) with archived-tournament history to derive yearStartElo / activity /
//    30-day trend. Pure.
// ─────────────────────────────────────────────────────────────────────
import { getTeamPlayerIds } from './padel24';

// The demo bank moved to @starpoint/engine (#272) so the database seed script
// and the app agree about what it is — re-exported here, unchanged, because
// every existing import and test names it through this module.
export { normalizeForEmail, buildPlayerDB } from '@starpoint/engine';
import { buildPlayerDB } from '@starpoint/engine';

export const PLAYER_DB: any[] = buildPlayerDB();

export function getEffectivePlayerDB(
  archive: any,
  playersList: any = null,
  // WHAT AN EMPTY LIST FALLS BACK TO. The demo bank by default — the blob
  // backend's local database. On the API the caller passes `[]` (#1060): there
  // the bank's ids are real platform rows the club was never shown.
  fallback: any[] = PLAYER_DB,
): any[] {
  const currentYear = new Date().getFullYear();
  const yearStartTs = new Date(currentYear, 0, 1).getTime(); // January 1st 00:00 this year
  const thirtyDaysAgoTs = Date.now() - 30 * 24 * 60 * 60 * 1000;

  const db = new Map();

  // Use the provided list or fall back to PLAYER_DB
  const sourceList = playersList && playersList.length > 0 ? playersList : fallback;

  sourceList.forEach((p) => {
    const baseline = p.originalElo ?? p.elo;
    db.set(p.id, {
      ...p,
      originalElo: baseline,
      yearStartElo: baseline,
      tournamentCount: 0,
      tournamentCountThisYear: 0,
      // Activity + trend:
      lastPlayedAt: 0, // timestamp of the last match the player participated in
      elo30dAgo: null, // ELO as it was ~30 days ago (for the trend arrow)
    });
  });

  // Sort the archive oldest first so that newer overrides older
  const sorted = [...(archive || [])].sort(
    (a, b) => (a.data?.archivedAt || 0) - (b.data?.archivedAt || 0),
  );

  // Players who have had yearStartElo set from the archive (from either last-year
  // or this-year data). Used to know which players still have baseline as
  // yearStartElo and need it updated from the first in-year match's playerEloPre.
  const yearStartUpdated = new Set();

  sorted.forEach((item) => {
    const t = item.data;
    if (!t || !Array.isArray(t.players)) return;
    const archivedAt = t.archivedAt || 0;
    const isFromThisYear = archivedAt >= yearStartTs;

    t.players.forEach((p) => {
      const existing = db.get(p.id);
      if (existing) {
        // IMPORTANT: Do not overwrite elo here — allPlayers is the authoritative
        // source for the current ELO (updated by both tournaments and single matches).
        let nextYearStart = existing.yearStartElo;

        if (!isFromThisYear) {
          // Tournament from last year or earlier — the final value of this one becomes
          // the starting point for the current year (the last last-year tournament wins).
          nextYearStart = p.elo;
          yearStartUpdated.add(p.id);
        } else if (!yearStartUpdated.has(p.id)) {
          // The player's FIRST this-year tournament — fetch playerEloPre from the first
          // match to reconstruct the ELO BEFORE they played this year. Without this,
          // yearStartElo would stay at baseline (current_elo) and the report
          // "ELO at year start vs. now" would always show change=0.
          const firstMatch = (t.matches || [])
            .filter((m) => m.completed && m.playerEloPre?.[p.id] != null)
            .sort(
              (a, b) => (a.playedAt || a.completedAt || 0) - (b.playedAt || b.completedAt || 0),
            )[0];
          if (firstMatch) {
            nextYearStart = firstMatch.playerEloPre[p.id];
            yearStartUpdated.add(p.id);
          }
        }

        // Activity: find the last match timestamp the player participated in.
        // And trend: find playerEloPre from the FIRST match that is newer
        // than 30 days (= the player's ELO at the start of the 30-day window).
        let lastPlayedAt = existing.lastPlayedAt || 0;
        let elo30dAgo = existing.elo30dAgo;
        (t.matches || []).forEach((m) => {
          if (!m.completed) return;
          const [t1p1, t1p2] = getTeamPlayerIds(m.team1);
          const [t2p1, t2p2] = getTeamPlayerIds(m.team2);
          if (![t1p1, t1p2, t2p1, t2p2].includes(p.id)) return;
          const ts = m.playedAt || m.completedAt || archivedAt;
          if (ts > lastPlayedAt) lastPlayedAt = ts;
          // ELO 30 days back: the earliest match within the window with playerEloPre
          if (ts >= thirtyDaysAgoTs && m.playerEloPre?.[p.id] != null) {
            if (elo30dAgo == null || ts < (existing.__elo30dTs || Infinity)) {
              elo30dAgo = m.playerEloPre[p.id];
              existing.__elo30dTs = ts;
            }
          }
        });

        db.set(p.id, {
          ...existing,
          // elo: keep from sourceList (allPlayers), not from the archive
          yearStartElo: nextYearStart,
          tournamentCount: (existing.tournamentCount || 0) + 1,
          tournamentCountThisYear:
            (existing.tournamentCountThisYear || 0) + (isFromThisYear ? 1 : 0),
          lastPlayedAt,
          elo30dAgo,
          __elo30dTs: existing.__elo30dTs,
        });
      }
    });
  });

  // Remove internal helper fields before returning
  return Array.from(db.values()).map((p) => {
    const { __elo30dTs, ...clean } = p;
    return clean;
  });
}

// ─── Player tournament history (across the archive) — PR-AO ───

export { getPlayerTournamentHistory } from '@starpoint/engine/playerHistory';
